import {
  useTemplateSecrets,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import { useEffect, useState } from 'react';
import {
  useApi,
  createApiRef,
  microsoftAuthApiRef,
  configApiRef,
  OpenIdConnectApi,
  ProfileInfoApi,
  BackstageIdentityApi,
  SessionApi,
} from '@backstage/core-plugin-api';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

const cloudIdpAuthApiRef = createApiRef<
  OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
>({
  id: 'auth.cloudidp-provider',
});

export const AuthTokenResolver = ({
  onChange,
}: FieldExtensionComponentProps<any>) => {
  const { setSecrets } = useTemplateSecrets();
  const cloudIdpAuthApi = useApi(cloudIdpAuthApiRef);
  const microsoftAuthApi = useApi(microsoftAuthApiRef);
  const config = useApi(configApiRef);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [provider, setProvider] = useState<string>('');

  useEffect(() => {
    const fetchToken = async () => {
      try {
        setLoading(true);

        const idToken = await cloudIdpAuthApi
          .getIdToken({ optional: true })
          .catch(() => null);

        if (idToken) {
          setSecrets({ AUTH_TOKEN: idToken });
          onChange(idToken);
          setProvider('CloudIDP');
          return;
        }

        // Fallback to Microsoft access token
        const scope = config.getString('megatron.papi.scope');
        const accessToken = await microsoftAuthApi.getAccessToken(scope);
        setSecrets({ AUTH_TOKEN: accessToken });
        onChange(accessToken);
        setProvider('Microsoft');
      } catch (err: any) {
        setError(
          `Failed to fetch auth token: ${
            err instanceof Error ? err.message : String(err)
          }`,
        );
      } finally {
        setLoading(false);
      }
    };

    fetchToken();
  }, [cloudIdpAuthApi, microsoftAuthApi, config, setSecrets, onChange]);

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 1.5,
          padding: 1.5,
        }}
      >
        <ErrorIcon sx={{ fontSize: 24, color: 'error.main' }} />
        <Typography variant="body1" sx={{ fontWeight: 500 }} color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return loading ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 1.5,
        padding: 1.5,
      }}
      aria-live="polite"
    >
      <CircularProgress
        color="primary"
        aria-label="Fetching auth token"
        title="Fetching auth token"
        sx={{
          animationDuration: '550ms',
          width: '24px !important',
          height: '24px !important',
        }}
      />
      <Typography variant="body2" sx={{ fontWeight: 500 }} color="primary">
        Retrieving auth token. Please wait...
      </Typography>
    </Box>
  ) : (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 1,
        padding: 1.5,
      }}
    >
      <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
      <Typography variant="body1" sx={{ fontWeight: 600 }} color="primary">
        Auth token retrieved successfully via {provider}
      </Typography>
    </Box>
  );
};
